import {
  Group,
  Card,
  Title,
  Image,
  Text,
  Select,
  Menu,
  ActionIcon,
  Avatar,
  Box,
  Divider,
  Paper,
  Center,
  Button,
  LoadingOverlay,
  Loader,
} from "@mantine/core";
import classes from "./styles.module.css";
import logo from "../../assets/ramboll-logo.svg";
import bgImage from "../../assets/rims_bg_launch_screen.png"; // Adjust the filename as needed
import { useEffect, useState } from "react";
// import { getAddressSystems } from "../../services/UserRoleService"; // Removed - security fix
import useTransientStore from "../../data/transcientStore";
import { useNavigate } from "react-router";
import { IconLogout, IconAlertTriangle, IconTool } from "@tabler/icons-react";
import { useMsal } from "@azure/msal-react";
import { AUTH_ENABLED } from "../../data/authConfig";
import {
  getQualityControlByAddressSystem,
  getStatusByAddressSystem,
} from "../../services/MapComponentsService";
import { usePermissions } from "../../hooks/usePermissions";
import { useTranslation } from "../../hooks/useTranslation";
import { getClientSystemSettings } from "../../services/ClientSystemSettingsService";
import { allowedDevUsers } from "../../constants/devConstants";

const backgroundImageUrl = bgImage;

export default function AddressSelection() {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const currentUserAddressSystems = useTransientStore(
    (state) => state.currentUserAddressSystems,
  );
  // Use first assigned system for contract-scoped page text when none selected yet (avoids addressSystemId=0 empty API + fetch loop)
  const translationAddressSystemId =
    selectedAddressSystem?.id ??
    (currentUserAddressSystems.length > 0 ? currentUserAddressSystems[0].id : 0);

  const { getTranslation, loading: translationsLoading } = useTranslation({
    pageId: 339,
    addressSystemId: translationAddressSystemId,
    autoLoad: true,
  });

  const addressSelectionTitle = getTranslation("LABEL_ADDRESS_SELECTION_TITLE");
  const chooseAddressPlaceholder = getTranslation(
    "PLACEHOLDER_CHOOSE_YOUR_ADDRESS_SYSTEM",
  );
  const addressSelectionHelp = getTranslation("LABEL_ADDRESS_SELECTION_HELP");

  const fontSizeForLabel = (
    label: string | null | undefined,
    key: string,
    normalSize: number,
  ) => (!label || label === key ? 16 : normalSize);
  const currentUserRole = useTransientStore((state) => state.currentUserRole);
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const currentUserEmail = useTransientStore((state) => state.currentUserEmail);
  const isGlobalAdmin = currentUserRole === 1;
  const currentUserIdentifier = `${currentUserId}-${currentUserEmail}`;
  const isDevUser = allowedDevUsers.includes(currentUserIdentifier);
  const setSelectedAddressSystem = useTransientStore(
    (state) => state.setSelectedAddressSystem,
  );
  const selectedAddressSystemStatuses = useTransientStore(
    (state) => state.selectedAddressSystemStatuses,
  );
  const qualityControls = useTransientStore(
    (state) => state.selectedAddressSystemQualityControls,
  );
  const setToken = useTransientStore((state) => state.setToken);
  const currentUser = useTransientStore((state) => state.currentUser);
  const resetSession = useTransientStore((state) => state.resetSession);
  const setClientSystemSettings = useTransientStore(
    (state) => state.setClientSystemSettings,
  );
  const navigate = useNavigate();
  const { instance: msalInstance } = useMsal();
  const [value, setValue] = useState<string | null>("");
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Permissions hook
  const { loadPermissions } = usePermissions();

  // Clear initial load flag after first render cycle
  useEffect(() => {
    setInitialLoad(false);
  }, []);

  // Set document title
  useEffect(() => {
    // Wait for translations to load before setting title
    if (translationsLoading) return;

    const title = getTranslation("TITLE_RIMS_ADDRESS_SELECTION");
    document.title =
      !title || title === "TITLE_RIMS_ADDRESS_SELECTION"
        ? "RIMS - Address Selection"
        : title;
  }, [getTranslation, translationsLoading]);

  useEffect(() => {
    const loadAddressSystemData = async (system: any) => {
      try {
        setLoading(true);
        // Load statuses
        const statuses = await getStatusByAddressSystem(system.id);
        useTransientStore.getState().setSelectedAddressSystemStatuses(statuses);

        // Load quality controls
        const qualityControls = await getQualityControlByAddressSystem(
          system.id,
        );
        useTransientStore
          .getState()
          .setSelectedAddressSystemQualityControls(qualityControls);

        // Load client system settings
        const settings = await getClientSystemSettings(system.id);
        const settingsMap = new Map(Object.entries(settings));
        setClientSystemSettings(settingsMap);

        // Load permissions
        await loadPermissions();

        // Navigate to dashboard
        navigate("/dashboard");
      } catch (error) {
        console.error("Failed to load address system data:", error);
        setLoading(false);
      }
    };

    // Auto-navigate global admins to dashboard even without address systems
    if (isGlobalAdmin && currentUserAddressSystems.length === 0) {
      navigate("/dashboard");
      return;
    }

    if (selectedAddressSystem.id > 0) {
      loadAddressSystemData(selectedAddressSystem);
    }

    if (currentUserAddressSystems.length === 1) {
      // If only one address system, select it automatically
      const system = currentUserAddressSystems[0];
      setSelectedAddressSystem(system);
      loadAddressSystemData(system);
    }

    if (value !== null && value !== "") {
      const selectedSystem = currentUserAddressSystems.find(
        (system) => system.id.toString() === value,
      );
      if (selectedSystem) {
        setSelectedAddressSystem(selectedSystem);
        loadAddressSystemData(selectedSystem);
      }
    }
  }, [
    currentUserAddressSystems,
    currentUserRole,
    value,
    setSelectedAddressSystem,
    selectedAddressSystem,
    setClientSystemSettings,
    loadPermissions,
    navigate,
    isGlobalAdmin,
  ]);

  const handleLogout = async () => {
    setToken(null);
    resetSession();
    if (!AUTH_ENABLED) {
      navigate("/");
      return;
    }
    try {
      await msalInstance.logoutRedirect({
        account:
          msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0],
        postLogoutRedirectUri: "/",
        onRedirectNavigate: () => true,
      });
    } catch (e) {
      console.error("Logout redirect failed", e);
      navigate("/");
    }
  };

  // Show loader immediately for single-address users (they'll be auto-redirected)
  // Also show during data loading or translations loading
  // initialLoad ensures we show loader on first render before translationsLoading becomes true
  // This avoids showing multiple sequential loading states
  if (
    loading ||
    currentUserAddressSystems.length === 1 ||
    translationsLoading ||
    initialLoad
  ) {
    return (
      <div
        style={{
          minHeight: "100vh",
          width: "100vw",
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Center style={{ width: "100vw", height: "100vh" }}>
          <Loader size={48} color="blue" type="bars" />
        </Center>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {/* No Address System Access message for non-admin users with no address systems */}
      {currentUserAddressSystems.length === 0 && (
        <Paper
          radius="lg"
          shadow="xl"
          p="xl"
          style={{
            backdropFilter: "blur(8px)",
            background: "rgba(255,255,255,0.95)",
            border: "1px solid rgba(255,255,255,0.6)",
            width: "100%",
            maxWidth: 450,
            textAlign: "center",
            position: "relative",
          }}
        >
          <LoadingOverlay
            visible={isGlobalAdmin}
            overlayProps={{ radius: "lg", blur: 15 }}
            loaderProps={{ color: "#009DF0", type: "bars" }}
          />
          <Center mb="md">
            <IconAlertTriangle size={48} color="#fa5252" />
          </Center>
          <Title order={3} mb="sm" style={{ color: "#005eb8" }}>
            No Address System Access
          </Title>
          <Text size="md" c="dimmed" mb="lg">
            You are not assigned to any address system. Please contact your
            administrator to request access.
          </Text>
          {/* Developer bypass - allows access to admin tabs */}
          {isDevUser && !isGlobalAdmin && (
            <Button
              variant="filled"
              color="orange"
              onClick={() => navigate("/dashboard")}
              mb="sm"
              fullWidth
              leftSection={<IconTool size={18} />}
            >
              Dev Mode: Dashboard Access
            </Button>
          )}
          <Button
            variant="outline"
            color="blue"
            onClick={handleLogout}
            leftSection={<IconLogout size={18} />}
          >
            Logout
          </Button>
        </Paper>
      )}

      {/* User menu (logout) */}
      {currentUserAddressSystems.length > 0 && (
        <div style={{ position: "fixed", top: 12, right: 16, zIndex: 200 }}>
          <Menu withArrow position="bottom-end">
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                aria-label={getTranslation("ARIA_USER_MENU") || "User Menu"}
                size="xl"
              >
                <Avatar
                  style={{ background: "white" }}
                  name={currentUser || "User"}
                  size="lg"
                />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>
                {getTranslation("LABEL_ADDRESS_SELECTION_SESSION") || "Session"}
              </Menu.Label>
              <Menu.Item
                leftSection={<IconLogout size={18} />}
                onClick={handleLogout}
              >
                {getTranslation("LABEL_ADDRESS_SELECTION_LOGOUT") || "Logout"}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      )}
      {currentUserAddressSystems.length > 0 && (
        <Card
          className={classes.form}
          radius="lg"
          shadow="xl"
          padding="xl"
          style={{
            backdropFilter: "blur(8px)",
            background: "rgba(255,255,255,0.85)",
            border: "1px solid rgba(255,255,255,0.6)",
            width: "100%",
            maxWidth: 400,
            minHeight: 320,
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <LoadingOverlay
            visible={loading}
            overlayProps={{ radius: "lg", blur: 15 }}
            loaderProps={{ color: "#009DF0", type: "bars" }}
          />
          <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Title
              order={2}
              ta="center"
              mb={20}
              style={{
                fontWeight: 700,
                fontSize: fontSizeForLabel(
                  addressSelectionTitle,
                  "LABEL_ADDRESS_SELECTION_TITLE",
                  30,
                ),
                color: "#005eb8",
                letterSpacing: 0.5,
              }}
            >
              {addressSelectionTitle || "Select Address System"}
            </Title>
            <Select
              data={currentUserAddressSystems.map((system) => ({
                value: system.id.toString(),
                label: system.name,
              }))}
              value={value}
              onChange={(e) => {
                setValue(e ? e.trim() : e);
              }}
              searchable
              placeholder={
                chooseAddressPlaceholder || "Choose your address system"
              }
              size="md"
              radius="md"
              styles={{
                input: {
                  fontSize: fontSizeForLabel(
                    chooseAddressPlaceholder,
                    "PLACEHOLDER_CHOOSE_YOUR_ADDRESS_SYSTEM",
                    18,
                  ),
                  padding: "12px 16px",
                  minWidth: 0,
                  width: "100%",
                },
                dropdown: {
                  fontSize: fontSizeForLabel(
                    chooseAddressPlaceholder,
                    "PLACEHOLDER_CHOOSE_YOUR_ADDRESS_SYSTEM",
                    16,
                  ),
                },
                root: { width: "100%" },
              }}
              className={classes["address-select"]}
            />
            <Text
              size="sm"
              style={{
                marginTop: 12,
                textAlign: "center",
                color: "#666",
                fontSize: fontSizeForLabel(
                  addressSelectionHelp,
                  "LABEL_ADDRESS_SELECTION_HELP",
                  14,
                ),
              }}
            >
              {addressSelectionHelp ||
                "Please select the address system you want to work with."}
            </Text>
          </Box>
          <Group
            justify="center"
            mt="lg"
            gap={8}
            wrap="nowrap"
            style={{ flexDirection: "row", alignItems: "center" }}
          >
            <Image
              src={logo}
              alt="Ramboll logo"
              maw={150}
              style={{ display: "inline-block" }}
            />
            <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              &copy; RIMS {new Date().getFullYear()}
            </Text>
          </Group>
        </Card>
      )}
    </div>
  );
}
